import {ChangeDetectorRef, Component, OnDestroy, OnInit, Renderer2} from '@angular/core';
import {Title} from '@angular/platform-browser';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet,
  RoutesRecognized
} from '@angular/router';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {BehaviorSubject, Observable, of, Subject, switchMap} from 'rxjs';
import {delay, filter, map, takeUntil, tap} from 'rxjs/operators';
import {coreEnvironment} from 'src/environments/core/core.environment';
import {environment} from 'src/environments/environment';

import {AlertService} from './services';
import {Globals} from './shared/configuration/globals';
import {FullyLoggedInStateType} from './tinea-components/okta/enum/FullyLoggedInStateType';
import {LoggedInService} from './tinea-components/okta/services/loggedIn.service';
import {OktaTestCommandsService} from './tinea-components/okta/services/testing/okta-test-commands.service';
import {UiLoadingModel} from './tinea-components/loading/models/UiLoadingModel';
import {importLibrary, setOptions} from '@googlemaps/js-api-loader';
import {OktaAuthEventListener} from './tinea-components/okta/services/auto-listener/okta-auth-event-listener.service';
import {DemoService} from './services/demo/demo.service';
import {LeftNavigationComponent} from 'src/app/modules/left-navigation/left-navigation.component';
import { AsyncPipe, NgClass } from '@angular/common';
import {AlertComponent} from 'src/app/shared/components/notifications/alert/alert.component';
import {LoadingComponent} from 'src/app/tinea-components/loading/component/loading/loading.component';
import {FeatureTogglePanelComponent} from 'src/app/shared/components/feature-toggle-panel/feature-toggle-panel.component';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import {PrimeNG} from 'primeng/config';

dayjs.locale('fr');

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [
    TranslatePipe,
    LeftNavigationComponent,
    NgClass,
    RouterOutlet,
    AlertComponent,
    AsyncPipe,
    LoadingComponent,
    FeatureTogglePanelComponent,
]
})
export class AppComponent implements OnInit, OnDestroy {
  previousUrl!: string;
  currentUrlSlug!: string;
  loggedIn = false;
  isDemoMode = false;
  fullSidebar = false;
  quickAccessOpen = false;
  enableAutoCollapse = false;

  route!: string;
  maintenance = coreEnvironment.maintenance;
  loadingModel = new UiLoadingModel();

  displayNotification$: Observable<boolean> = of(false);
  demoServiceProgress$: BehaviorSubject<number>;

  destroyed$ = new Subject<void>();

  get mainClasses(): Record<string, boolean> {
    const sidebarVisible = this.isSidebarVisible();
    return {
      'logged-out': !this.loggedIn,
      'right-content': sidebarVisible && this.fullSidebar,
      'right-content--extended': sidebarVisible && !this.fullSidebar,
      'demo--active': this.isDemoMode,
    };
  }

  constructor(
    private router: Router,
    private renderer: Renderer2,
    private titleService: Title,
    private loggedInService: LoggedInService,
    private translate: TranslateService,
    private globals: Globals,
    private alertService: AlertService,
    private oktaAuthEventService: OktaAuthEventListener,
    private oktaTestCommandsService: OktaTestCommandsService,
    private demoService: DemoService,
    private changeDetectorRef: ChangeDetectorRef,
    private readonly config: PrimeNG,
  ) {
    this.config.setTranslation(this.translate.instant('primeng'));
    // subscribe to alert observable &
    // simulate delaying it's execution in case we need to show the message right on application start
    this.displayNotification$ = this.alertService.currentDisplay.pipe(delay(0));

    this.demoServiceProgress$ = this.demoService.progress$;


    // this language will be used as a fallback when a translation isn't found in the current language
    this.translate.setFallbackLang('fr'); // We use french since it's the most translated language for now
    this.router.events.subscribe((e) => {
      if (e instanceof NavigationStart) {
        this.loadingModel.isLoading = true;
      } else if (e instanceof NavigationEnd) {
        this.loadingModel.isLoading = false;
        this.route = e.url;
        document.documentElement.scrollTop = 0;
      } else if (e instanceof NavigationCancel || e instanceof NavigationError) {
        this.loadingModel.isLoading = false;
      }
    });

    this.changeDetectorRef.reattach();

    const languageStored = localStorage.getItem('language');

    if (languageStored) {
      this.translate.use(languageStored);
    } else {
      /*  If browser language not in the list, use default language. */
      const browserLanguage = this.translate.getBrowserLang();
      if (this.globals.languages.find((lang) => lang.name === browserLanguage) === undefined || !browserLanguage) {
        this.translate.use(this.translate.getFallbackLang()!);
        localStorage.setItem('language', this.translate.getFallbackLang()!);
      } else {
        this.translate.use(browserLanguage);
        localStorage.setItem('language', browserLanguage);
      }
    }

    // get url & set body class depending on space
    this.setBodyClass();
  }

  ngOnInit() {
    this.isLoggedPropertyObserve();
    this.loadGoogleMapsApi().then();

    this.oktaAuthEventService.initTokenEvents();

    this.demoService.isDemoMode$.pipe(
      delay(5), // slight delay to prevent potential race
      takeUntil(this.destroyed$)
    )
      .subscribe((value) => {
        this.isDemoMode = value;
      });

    // Uncomment for manually trigger token renewal in development mode
    // this.oktaTestCommandsService.register();
  }

  isLoggedPropertyObserve() {
    this.loggedInService
      .isLoggedIn$()
      .pipe(
        tap((isLoggedIn) => this.setLoggedInProperty(isLoggedIn)),
        switchMap(() => this.loggedInService.fullyLoggedInState$.pipe(map((state) => state === FullyLoggedInStateType.SUCCESS))),
        takeUntil(this.destroyed$),
      )
      .subscribe((isFullyLoggedIn) => {
        this.setLoggedInProperty(isFullyLoggedIn);
      });
  }

  loadGoogleMapsApi() {
    setOptions({ key: 'AIzaSyCAk4wMdQNgKDTh7r3Fj-SIAtRruq_PAYo' });
    return importLibrary('visualization');
  }

  setLoggedInProperty(value: boolean) {
    this.loggedIn = value;
  }

  setBodyClass() {
    // take event that contains the final route,
    // otherwise irrelevant url slugs like type errors will become body class
    this.router.events.pipe(filter((e) => e instanceof RoutesRecognized)).subscribe((event: RoutesRecognized) => {
      if (this.previousUrl && this.previousUrl !== '/') {
        this.renderer.removeClass(document.body, this.previousUrl.split('/')[1]);
      }
      this.currentUrlSlug = event.urlAfterRedirects.split('/')[1];
      if (this.currentUrlSlug === '') {
        this.renderer.addClass(document.body, 'home');
      }

      if (this.currentUrlSlug) {
        this.renderer.removeClass(document.body, 'home');
        this.renderer.addClass(document.body, this.currentUrlSlug);
      }

      // set url for redirecting user after login (TODO: currently, avoiding sites list, to be changed when several sites view available)
      this.previousUrl = event.urlAfterRedirects;
      if (!this.previousUrl.includes('login') && !this.previousUrl.includes('implicit/callback') && this.previousUrl !== '/sites') {
      }
    });

    this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
      this.setTitle(this.currentUrlSlug);
    });
  }

  /**
   * Set the relevant title for each page, that will be visible in history.
   */
  public setTitle(newTitle: string) {
    let formattedTitle: string = newTitle;
    if (formattedTitle.includes('?')) {
      formattedTitle = formattedTitle.split('?')[0];
    }

    const translatedTitle: string = this.translate.instant(`menu.${formattedTitle}`);

    let fullNewTitle = environment.core.title;
    if (!!translatedTitle && !translatedTitle.includes('menu.')) {
      fullNewTitle = environment.core.title + ' - ' + translatedTitle;
    }

    this.titleService.setTitle(fullNewTitle);
  }

  isSidebarVisible(): boolean {
    return this.loggedIn && !this.isDemoMode;
  }

  // Recheck sidebar state when closing quick access panel
  onPanelToggle(open: boolean): void {
    if (!this.enableAutoCollapse) {
      this.quickAccessOpen = open;
      if (!open) {
        this.fullSidebar = false;
      }
    }
  }

  onAutoCollapseChange(active: boolean): void {
    this.enableAutoCollapse = active;
  }

  onMouseover(): void {
    if (!this.enableAutoCollapse) this.fullSidebar = true;
  }

  onMouseout(): void {
    if (!this.enableAutoCollapse) this.fullSidebar = this.quickAccessOpen;
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();

    // Uncomment if oktaTestCommandsService.register() is needed in development mode
    //this.oktaTestCommandsService.unregister();
  }
}
